import { useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { TapCue } from './TapCue';

type Rect = { x: number; y: number; width: number; height: number };
export function ContentGuide({
  target,
  button,
  onSettled,
  onSkip,
}: {
  target: RefObject<HTMLDivElement | null>;
  button: RefObject<HTMLButtonElement | null>;
  onSettled: (value: boolean) => void;
  onSkip: () => void;
}) {
  const [geometry, setGeometry] = useState<{
    card: Rect;
    button: Rect;
    width: number;
    height: number;
  } | null>(null);
  const [settled, setSettled] = useState(false);
  const settledRef = useRef(false);
  useEffect(() => {
    let frame = 0;
    let resizeFrame = 0;
    let lastY = -1;
    let stable = 0;
    let started = 0;
    let moving = true;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const measure = () => {
      const card = target.current?.getBoundingClientRect();
      const action = button.current?.getBoundingClientRect();
      if (card && action) setGeometry({ card, button: action, width: innerWidth, height: innerHeight });
    };
    const finish = () => {
      if (!moving) return;
      moving = false;
      settledRef.current = true;
      setSettled(true);
      onSettled(true);
      target.current?.focus({ preventScroll: true });
      measure();
    };
    const track = (now: number) => {
      measure();
      const rect = target.current?.getBoundingClientRect();
      stable = Math.abs(scrollY - lastY) < 0.5 ? stable + 1 : 0;
      lastY = scrollY;
      if (rect && stable > 4 && now - started >= (reduced ? 0 : 850) && Math.abs(rect.top + rect.height / 2 - innerHeight / 2) < 3) finish();
      if (now - started > 2200) finish();
      // Keep the cutout and arrow aligned while the footer finishes its entrance.
      if (moving || now - started < 900) frame = requestAnimationFrame(track);
    };
    const begin = () => {
      const rect = target.current?.getBoundingClientRect();
      if (!rect) return;
      moving = true;
      stable = 0;
      settledRef.current = false;
      setSettled(false);
      onSettled(false);
      started = performance.now();
      window.scrollTo({
        top: Math.max(0, scrollY + rect.top + rect.height / 2 - innerHeight / 2),
        behavior: reduced ? 'instant' : 'smooth',
      });
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(track);
    };
    const onScroll = () => {
      if (!moving) measure();
    };
    const onResize = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(begin);
    };
    const onScrollEnd = () => {
      const rect = target.current?.getBoundingClientRect();
      if (rect && performance.now() - started >= (reduced ? 0 : 850) && Math.abs(rect.top + rect.height / 2 - innerHeight / 2) < 3) finish();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onSkip();
    };
    frame = requestAnimationFrame(begin);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('scrollend', onScrollEnd);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(resizeFrame);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('scrollend', onScrollEnd);
      document.removeEventListener('keydown', onKey);
    };
  }, [target, button, onSettled, onSkip]);
  if (!geometry) return null;
  const { button: action } = geometry;
  return createPortal(
    <div className="content-guide" data-settled={settled} aria-hidden="true">
      {settled && <TapCue x={action.x + action.width / 2 + 16} y={action.y + 28} />}
    </div>,
    document.body,
  );
}
